/* eslint-disable react-hooks/exhaustive-deps */
/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/Modules/Auth/pages/AuthPage`, `src/app/BaseRoute`).
 */
import React, { lazy, useContext, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Redirect,
  Switch,
  Route,
  useLocation,
  useHistory,
  withRouter,
} from "react-router-dom";

import { SignUp } from "../app/Components/PopupLogin/signupAsset";

import { ParticipantSignup } from "./Components/PopupLogin/ParticipantSignup";
import { MatomoContext } from "./MatomoProvider";
import { Logout, AuthPage } from "./Modules/Auth";
import { ErrorPage1 } from "./Modules/ErrorsExamples/ErrorPage1";
import ErrorsPage from "./Modules/ErrorsExamples/ErrorsPage";
import HomePage from "./NewPages/Homepage";
import { SearchResult } from "./NewPages/SearchResult";
import { ForceResetPassword } from "./Pages/ForceResetPassword";
import { allOk } from "./Pages/WebsitePin/verifyCrud";
import {
  ASSET_CATEGORY,
  TRANSPORT_COMPANY_FORM,
  FINANCE_COMPANY_FORM,
  BUY,
  SELL,
  ABOUT_US,
  CAREERS,
  NEWS,
  FAQS,
  SITEMAP,
  COMPLETE_PROFILE,
  SEARCH,
  READ_ALL_NOTOFICATIONS,
  DEALS_TAB,
  WORKING,
  UPCOMING_FEATURES,
  SHIPPED_COMPANY_FORM,
  BETA_TEASTER,
  PLATFORM_HEALTH,
  MOBILE_APP,
  SET_PASSWORD,
  COMPLETE_PROFILE_PARTICIPANTS,
  PATH_360_PAGE,
  CATEGORY_PAGE_PATH,
  FORCE_PASSWORD,
  VERIFICATION,
  AUTH,
  ADMIN,
  LOGOUT,
  ERROR,
  VERIFY_EMAIL,
  ABC,
  SEARCH_RESULT,
  HOMEPAGE_PAGE,
} from "./Utils/routePaths";

const ClientRoute = lazy(() => import("./ClientRoute"));

const VerifyEmail = lazy(() =>
  import("./Modules/Auth/pages/VerifyEmail"),
);

const WebsitePin = lazy(() => import("./Pages/WebsitePin"));

const BaseRoute = lazy(() => import("./BaseRoute"));

export const Routes = withRouter(() => {
  const {
    isAuthorized,
    displayName,
    email,
    userId,
    pin,
    currentOrganization,
    role,
  } = useSelector(
    ({ auth }) => ({
      isAuthorized:
        auth.user != null && auth.user.roles && auth.user.roles.length > 0,
      displayName: auth.user != null && auth.user.displayName,
      email: auth.user != null && auth.user.email,
      userId: auth.user != null && auth.user.id,
      currentOrganization:
        auth.user != null &&
        auth.user.currentOrganization &&
        auth.user.currentOrganization.name,
      role:
        auth.user != null &&
        auth.user.roles &&
        auth.user.roles.length > 0 &&
        auth.user.roles[0].key,
      pin: auth.pin,
    }),
    shallowEqual,
  );

  const [allowed, setAllowed] = useState("");

  const location = useLocation();

  const history = useHistory();

  const matomoContext = useContext(MatomoContext);

  useEffect(() => {
    async function checkPinData() {
      try {
        if (pin) {
          const ok = await allOk(pin);

          if (ok && ok.data && ok.data.message === "health ok") {
            setAllowed("");
            if (location.search.substr(0, 12) === "?redirectTo=") {
              const navigationAddress = location.search.substr(12);

              history.push(navigationAddress);
            }
          } else {
            setAllowed(`?redirectTo=${location.pathname}`);
          }
        } else {
          setAllowed(`?redirectTo=${location.pathname}`);
        }
      } catch (e) {
        setAllowed(`?redirectTo=${location.pathname}`);
      }
    }

    checkPinData();
  }, [history, pin]);

  useEffect(() => {
    matomoContext.trackUserPage("Homepage", location.pathname, [
      {
        loggedIn: isAuthorized,
      },
    ]);
  }, [location]);

  useEffect(() => {
    if (isAuthorized) {
      matomoContext.setLoggedInUser(
        userId,
        email,
        displayName,
        currentOrganization,
        role,
      );
    } else {
      matomoContext.setLoggedInUser("", "", "");
    }
  }, [isAuthorized]);

  return (
    <Switch>
      <Route exact path={VERIFICATION} component={WebsitePin} />
      {allowed !== "" && <Redirect to={`/verification${allowed}`} />}
      <Route exact path="/" component={ClientRoute} />
      <Route strict path={ASSET_CATEGORY} component={ClientRoute} />
      <Route strict path={SEARCH} component={ClientRoute} />
      <Route strict path={TRANSPORT_COMPANY_FORM} component={ClientRoute} />
      <Route strict path={FINANCE_COMPANY_FORM} component={ClientRoute} />
      <Route strict path={SHIPPED_COMPANY_FORM} component={ClientRoute} />
      <Route strict path={READ_ALL_NOTOFICATIONS} component={ClientRoute} />
      <Route strict path={ABOUT_US} component={ClientRoute} />
      <Route strict path={CAREERS} component={ClientRoute} />
      <Route strict path={WORKING} component={ClientRoute} />
      <Route strict path={NEWS} component={ClientRoute} />
      <Route strict path={SITEMAP} component={ClientRoute} />
      <Route strict path={FAQS} component={ClientRoute} />
      <Route strict path={ABC} component={ClientRoute} />
      <Route
        strict
        exact
        path={COMPLETE_PROFILE_PARTICIPANTS}
        component={ParticipantSignup}
      />
      <Route strict path={COMPLETE_PROFILE} component={SignUp} />
      <Route strict path={UPCOMING_FEATURES} component={ClientRoute} />
      <Route strict path={BETA_TEASTER} component={ClientRoute} />
      <Route strict path={BUY} component={ClientRoute} />
      <Route strict path={SELL} component={ClientRoute} />
      <Route strict path={PLATFORM_HEALTH} component={ClientRoute} />
      <Route strict path={MOBILE_APP} component={ClientRoute} />
      <Route strict path={DEALS_TAB} component={ClientRoute} />
      <Route strict path={PATH_360_PAGE} component={ClientRoute} />
      <Route strict path={CATEGORY_PAGE_PATH} component={ClientRoute} />
      <Route strict path={SET_PASSWORD} component={ClientRoute} />
      <Redirect exact path={AUTH} to={ADMIN} />
      <Route exact path={VERIFY_EMAIL} component={VerifyEmail} />
      <Route exact path={LOGOUT} component={Logout} />

      {/* New routes for new pages */}

      <Route strict path={SEARCH_RESULT} component={SearchResult} />
      <Route strict path={HOMEPAGE_PAGE} component={HomePage} />

      {!isAuthorized ? (
        <AuthPage />
      ) : (
        <>
          <Switch>
            <Route exact path={LOGOUT} component={Logout} />
            <Route exact path={FORCE_PASSWORD} component={ForceResetPassword} />
            <Route strict path={ADMIN} component={BaseRoute} />
            <Route component={ErrorPage1} />
          </Switch>
        </>
      )}
      <Route strict path={ERROR} component={ErrorsPage} />
      <Route component={ErrorPage1} />
    </Switch>
  );
});
